import { useEffect, useRef, useState } from 'react';

/**
 * Image with reserved space + optional shimmer behind it.
 * Eager images stay visible from the first paint (critical for LCP) —
 * we never start them at opacity:0.
 */
export default function SkeletonImage({
  src,
  alt,
  className = '',
  loading = 'lazy',
  width,
  height,
  sizes,
  fetchPriority,
  aspectRatio,
}) {
  const isEager = loading === 'eager';
  const [loaded, setLoaded] = useState(isEager);
  const imgRef = useRef(null);

  useEffect(() => {
    if (isEager) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src, isEager]);

  const ratio =
    aspectRatio ||
    (width && height ? `${width} / ${height}` : undefined);

  return (
    <span
      className={`skeleton-img${loaded ? ' is-loaded' : ''}${isEager ? ' is-eager' : ''}${
        className ? ` ${className}` : ''
      }`}
      style={ratio ? { aspectRatio: ratio } : undefined}
    >
      {!loaded && !isEager && <span className="skeleton-img__pulse" aria-hidden="true" />}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={loading}
        decoding={isEager ? 'sync' : 'async'}
        fetchPriority={fetchPriority || (isEager ? 'high' : 'auto')}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </span>
  );
}
